// src/hooks/useAuth.js

import { useState, useEffect, useContext, createContext } from "react";
import PropTypes from "prop-types";
import {
  initializeAuth,
  isAuthenticated as checkIsAuthenticated,
  getCurrentUser,
  getAuthToken,
  clearAuth,
  getLoginUrl,
  getEnvironmentConfig,
} from "../utils/auth";

// Create context for authentication
const AuthContext = createContext(null);

/**
 * AuthProvider component to wrap your app with
 * This ensures auth state is shared across components
 */
/**
 * Authentication provider component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const AuthProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [authError, setAuthError] = useState(null);

  // Get environment configuration
  const envConfig = getEnvironmentConfig();

  useEffect(() => {
    let isCancelled = false;
    const checkAuth = async () => {
      try {
        // Check if we're in local development mode
        if (envConfig.localEnabled) {
          try {
            // Local development authentication logic
            const response = await fetch("http://localhost:3002/retrieve");
            if (isCancelled) return;

            if (!response.ok) {
              throw new Error(`Local auth server error: ${response.status}`);
            }

            const data = await response.json();
            const { accessToken, user } = data;

            if (accessToken && user) {
              // Save to localStorage
              localStorage.setItem("accessToken", accessToken);
              localStorage.setItem("user", JSON.stringify(user));

              // Update state
              setToken(accessToken);
              setUser(user);
              setIsAuthenticated(true);
              setAuthError(null);
              console.log("Local auth successful");
            } else {
              // Invalid or incomplete auth data
              console.log("Received incomplete local auth data:", data);
              setIsAuthenticated(false);
              setAuthError("Incomplete authentication data received from local server");
            }
          } catch (localError) {
            console.error("Local authentication error:", localError);
            setAuthError(`Local auth failed: ${localError.message}`);
            setIsAuthenticated(false);
          }
        } else {
          // Standard authentication logic for non-local environments
          const { token, user } = initializeAuth();
          setToken(token);
          setUser(user);
          setIsAuthenticated(!!token);
          setAuthError(token ? null : "No valid authentication token found");
        }
      } catch (error) {
        console.error("Authentication error:", error);
        setAuthError(error.message);
        setIsAuthenticated(false);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    checkAuth();

    // Set up an interval to check auth status periodically
    // This can help detect if the user logs out in another tab
    // But use a longer interval to reduce overhead
    const interval = setInterval(checkAuth, 300000); // Check every 5 minutes

    return () => {
      isCancelled = true;
      clearInterval(interval);
    };
  }, [envConfig.localEnabled, envConfig.environment]);

  const logout = () => {
    clearAuth();
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);

    // Determine the appropriate home URL based on domain
    let homeUrl;

    // Domain-based detection for reliable redirects
    if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
      homeUrl = "http://localhost:3000";
    } else if (window.location.hostname.includes("chefaicookware.com")) {
      homeUrl = "https://chefaicookware.com";
    } else {
      // Fallback to configured URL or default
      homeUrl = envConfig.homeUrl || "https://chefaicookware.com";
    }

    // Log logout operation for debugging
    console.log("Logging out", {
      environment: envConfig.environment,
      localEnabled: envConfig.localEnabled,
      integrationEnabled: envConfig.integrationEnabled,
      homeUrl,
      hostname: window.location.hostname,
    });

    // Redirect to home page
    window.location.href = homeUrl;
  };

  // Context value
  const value = {
    isLoading,
    isAuthenticated,
    user,
    token,
    logout,
    envConfig,
    getLoginUrl,
    authError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// PropTypes validation
AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Custom hook for accessing authentication context
 * Must be used within an AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

/**
 * Hook for API calls with authentication
 * Provides a fetch wrapper that includes auth token
 */
export const useAuthenticatedFetch = () => {
  const { token, envConfig } = useAuth();

  const fetchWithAuth = async (url, options = {}) => {
    // Prepend API base URL if the URL doesn't start with http
    const fullUrl = url.startsWith("http") ? url : `${envConfig.apiBaseUrl}${url}`;

    const headers = {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    return fetch(fullUrl, {
      ...options,
      headers,
    });
  };

  return fetchWithAuth;
};
